import Format from './format';

/**
 * Classe para manipulação de máscara
 * 
 * @author Wellington Pereira
 * @since 2019-10-14
 * @version 1.0.0
 * @license Comercial
 */
export default class Mask {

  /**
   * Inicialização de variáveis
   */
  /**
   * @description Cria uma instância de Mask.
   * @author Wellington Pereira
   *
   * @constructor
   * @param {options} obj
   * @since 2019-10-14
   * @version 1.0.0
   */
  constructor(obj) {

    /**
     * Objeto HTML
     * 
     * @private
     * @type {HTMLobject}
     */
    this._obj = obj;

    /**
     * Tipo de tag
     * 
     * @private
     * @type {string}
     */
    this._type = this._obj.tagName.toUpperCase();

    /**
     * Padrão de pesquisa
     * 
     * @private
     * @type {RegularExpression}
     */
    this._pattern = null;

    /**
     * Máscara anterior
     * 
     * @private
     * @type {string}
     */
    this._oldMask = obj.oldMask;

    /**
     * Máscara
     * 
     * @private
     * @type {string}
     */
    this._mask = obj.maskText;

    /**
     * Opções de máscara
     * 
     * @private
     * @type {string}
     */
    this._maskOptions = {};
    const maskOptions = obj.maskOptions;

    if (typeof maskOptions === 'object') {
      this._maskOptions = maskOptions;
    } else if (typeof maskOptions === 'function') {
      const tempMaskOptions = maskOptions();
      if (tempMaskOptions) {
        this._maskOptions = tempMaskOptions;
      }
    } else if (typeof maskOptions === 'string') {
      try {
        this._maskOptions = JSON.parse(maskOptions);
      } catch (e) {
        this._maskOptions = {};
      }
    }

    if (!this._maskOptions.align) {
      this._maskOptions.align = 'left';
    }

    /**
     * Valor original
     * 
     * @private
     * @type {string}
     * @default {void}
     */
    this._oldValue = '';

    /**
     * Novo valor
     * 
     * @private
     * @type {string}
     * @default {void}
     */
    this._newValue = '';

    /**
     * @private
     * @instance
     * @type {instance}
     */
    this._format = new Format();
  }

  /**
   * Exclui a máscara de um objeto
   */
  delete() {
    this._oldValue = (this._type === 'INPUT') ? (this._obj.value) : (this._obj.innerText);
    this._newValue = '';

    const REs = {
      A: /[A-Za-z0-9\s\_\.\@\-]/,
      S: /[A-Za-z\s]/,
      C: /[A-Z]/,
      c: /[a-z]/,
      0: /[0-9]/,
      9: /[0-9\s]/
    };

    if (this._maskOptions?.patterns) {
      for (let mop in this._maskOptions.patterns) {
        REs[mop] = new RegExp(this._maskOptions.patterns[mop]);
      }
    }

    let pattern = '';
    for (let p in REs) {
      if (REs.hasOwnProperty(p)) {
        pattern += p;
      }
    }

    this._pattern = new RegExp('[' + pattern + ']');

    if (this._oldMask !== undefined) {
      for (let i = 0; i < this._oldValue.length; i++) {
        if (this._pattern.test(this._oldMask[i])) {
          for (let m in REs) {
            if (this._oldMask[i] === m && REs[m].test(this._oldValue[i])) {
              this._newValue += this._oldValue[i].toString();
            }
          }
        }
      }

      if (this._type === 'INPUT') {
        this._obj.value = this._newValue;
      } else {
        this._obj.innerText = this._newValue;
      }

      this._obj.style.textAlign = this._maskOptions.align;
    }
  }

  /**
   * Recupera a máscara de um objeto
   * 
   * @returns {string} mask
   */
  get() {
    return this._mask;
  }

  /**
   * Recupera o cursor de um objeto
   */
  getCaret() {
    let sel,
      pos = 0,
      dSel = document.selection,
      cSelStart = this._obj.selectionStart;

    // IE Support
    if (dSel && navigator.appVersion.indexOf('MSIE 10') === -1) {
      sel = dSel.createRange();
      sel.moveStart('character', -p.val().length);
      pos = sel.text.length;
    }
    // Firefox support
    else if (cSelStart || cSelStart === '0') {
      pos = cSelStart;
    }

    return pos;
  }

  /**
   * Recupera as opções de máscara
   * 
   * @returns {json} maskOptions
   */
  getOptions() {
    if (typeof this._maskOptions === 'string') {
      this._maskOptions = JSON.parse(this._maskOptions);
    }

    return this._maskOptions;
  }

  /**
   * Define uma máscara para um objeto
   */
  set() {
    this.delete();

    if (this._obj.maskOptions?.format) {
      this._mask = this._obj.maskOptions.format;
    }

    this._oldValue = (this._type === 'INPUT') ? (this._obj.value) : (this._obj.innerText);

    if (this._oldValue.startsWith('+')) {
      this._oldValue = this._oldValue.slice(1);
    }

    const numericTemplates = ['float', 'currency', 'percent', 'integer', 'number'];
    const numericTemplate = String(this._maskOptions?.template || '').toLowerCase();
    if (this._type !== 'INPUT' && numericTemplates.includes(numericTemplate)) {
      this._newValue = this._format.number(this._oldValue, {
        decimalPlaces: numericTemplate === 'integer' ? 0 : this._maskOptions.decimalPlaces,
        integer: numericTemplate === 'integer',
        currency: numericTemplate === 'currency' || this._maskOptions.currency === true,
        percent: numericTemplate === 'percent' || this._maskOptions.percent === true
      });
      this._obj.oldMask = this._mask;
      this._obj.textContent = this._newValue;
      this._obj.classList.add(this._maskOptions.align);
      return;
    }

    const digitsOnly = String(this._maskOptions?.template || '').toLowerCase() === 'cnpj'
      ? (this._oldValue || '').toUpperCase().replace(/[^0-9A-Z]/g, '')
      : (this._oldValue || '').replace(/\D/g, '');

    this._newValue = '';
    const REs = {
      A: /[A-Za-z0-9\s\_\.\@\-]/,
      S: /[A-Za-z\s]/,
      C: /[A-Za-z\s]/,
      c: /[a-z]/,
      0: /[0-9]/,
      9: /[0-9\s]/
    };

    if (typeof this._maskOptions === 'string') {
      this._maskOptions = JSON.parse(this._maskOptions);
    }

    if (this._maskOptions?.patterns) {
      for (let mop in this._maskOptions.patterns) {
        REs[mop] = new RegExp(this._maskOptions.patterns[mop]);
      }
    }

    let pattern = '';
    for (let p in REs) {
      pattern += p;
    }

    this._pattern = new RegExp('[' + pattern + ']');

    let j = 0;

    if (this._maskOptions.fn !== undefined && typeof this._maskOptions.fn === 'string') {
      switch (this._maskOptions.fn) {
        // case 'date':
        //   this._date();
        //   break;
        case 'time':
          this._time();
          break;
        case 'timeDays':
          this._timeDays();
          break;
        case 'timeTotal':
          this._timeTotal();
          break;
      }
    } else if (this._mask !== undefined && digitsOnly.length > 0) {
      for (let i = 0; i < this._mask.length; i++) {
        if (this._pattern.test(this._mask[i]) === false) {
          this._newValue += this._mask[i];
        } else {
          for (let m in REs) {
            if (this._mask[i] === m && digitsOnly[j] !== undefined && REs[m].test(digitsOnly[j])) {
              this._newValue += digitsOnly[j];
              j++;
            }
          }
        }

        if (j === digitsOnly.length) {
          break;
        }
      }
    }

    const floats = ['float', 'currency', 'percent'];
    if (this._maskOptions.template === 'integer' || (floats.includes(this._maskOptions.template) && this._maskOptions.decimalPlaces === 0)) {
      this._newValue = this._format.number(this._newValue, { integer: true });
    } else if (floats.includes(this._maskOptions.template)) {
      this._newValue = this._format.number(this._newValue, {
        decimalPlaces: this._maskOptions.decimalPlaces,
        currency: this._maskOptions.currency,
        percent: this._maskOptions.percent
      });
    }

    this._obj.oldMask = this._mask;

    if (this._type === 'INPUT') {
      this._obj.value = this._newValue;
    } else {
      this._obj.textContent = this._newValue;
    }


    this._obj.classList.add(this._maskOptions.align);
  }

  /**
   * Define o cursor em um objeto
   * 
   * @param {integer} pos 
   */
  setCaret(pos) {
    let range;

    // Firefox, WebKit, etc..
    if (this._obj.setSelectionRange) {
      this._obj.setSelectionRange(pos, pos);
    } else { // IE
      range = this._obj.createTextRange();
      range.collapse(true);
      range.moveEnd('character', pos);
      range.moveStart('character', pos);
      range.select();
    }
  }

  update(newMask) {
    if (typeof newMask === 'object') {
      this._maskOptions = newMask;
      this._mask = newMask.format;
      this._obj.maskOptions = newMask;
      this._obj.maskText = newMask.format;
    } else {
      this._mask = newMask;
      this._obj.maskOptions = { format: newMask, align: 'left', placeholder: newMask };
      this._obj.maskText = newMask;
    }

    this.set();
  }

  /**
   * Formata máscara de data
   */
  // _date() {
  //   let mask = this._mask;
  //   const formats = [
  //     ['Y-m-d', 'Y/m/d', 'Y.m.d'],
  //     ['y-m-d', 'y/m/d', 'y.m.d'],
  //     ['d-m-Y', 'd/m/Y', 'd.m.Y'],
  //     ['d-m-y', 'd/m/y', 'd.m.y'],
  //     ['m-d-Y', 'm/d/Y', 'm.d.Y'],
  //     ['m-d-y', 'm/d/y', 'm.d.y'],
  //   ];

  // const leap = (this._oldValue.substr(-2) % 4) === 0 ? true:false;
  // const d31 = ['01', '03', '05', '07', '08', '10', '12'];
  // const d30 = ['02', '04', '06', '09', '11'];
  // const df = leap ? 29 : 28;

  // let maskIndex = 0;
  // if (formats[2].includes(this._maskOptions.l18n)) {
  //   if (
  //     (d31.includes(this._oldValue.substr(2, 2)) && this._oldValue.substr(0, 2) <= 31) ||
  //     (d30.includes(this._oldValue.substr(2, 2)) && this._oldValue.substr(0, 2) <= 30) ||
  //     (this._oldValue.substr(2, 2) === '02' && this._oldValue.substr(0, 2) <= df)
  //   ) {
  //     this._newValue = this._oldValue.substr(0, 2);
  //   }
  // }


  // for (let i = 0; i < this._oldValue.length; i++) {
  // switch (i) {
  //   case 0:
  //     if (mask[0] === 'd') {
  //       if (/[0-3]/.test(this._oldValue[0])) {
  //         this._newValue += this._oldValue[0];
  //       }
  //     } else if (mask[0] === 'm') {
  //       if (/[01]/.test(this._oldValue[0])) {
  //         this._newValue += this._oldValue[0];
  //       }
  //     } else {
  //       if (/[0-2]/.test(this._oldValue[0])) {
  //         this._newValue += this._oldValue[0];
  //       }
  //     }
  //     break;
  //   case 1:
  //     if (mask[1] === 'd') {
  //       if (
  //         (/0/.test(this._oldValue[0]) && /[1-9]/.test(this._oldValue[1])) ||
  //         (/[12]/.test(this._oldValue[0]) && /[0-9]/.test(this._oldValue[1])) ||
  //         (/3/.test(this._oldValue[0]) && /[0-1]/.test(this._oldValue[1]))
  //       ) {
  //         this._newValue += this._oldValue[1] + mask[2];
  //       }
  //     } else if (mask[1] === 'm') {
  //       if (
  //         (/0/.test(this._oldValue[0]) && /[1-9]/.test(this._oldValue[1])) ||
  //         (/1/.test(this._oldValue[0]) && /[0-2]/.test(this._oldValue[1]))
  //       ) {
  //         this._newValue += this._oldValue[1] + mask[2];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[1])) {
  //         this._newValue += this._oldValue[1];
  //       }
  //     }
  //     break;
  //   case 2:
  //     if (mask[3] === 'd') {
  //       let m = this._oldValue.substr(0, 2);
  //       if (
  //         (m === '02' && /[0-2]/.test(this._oldValue[2])) ||
  //         (m !== '02' && /[0-3]/.test(this._oldValue[2]))
  //       ) {
  //         this._newValue += this._oldValue[2];
  //       }
  //     } else if (mask[3] === 'm') {
  //       if (/[01]/.test(this._oldValue[2])) {
  //         this._newValue += this._oldValue[2];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[2])) {
  //         this._newValue += this._oldValue[2];
  //       }
  //     }
  //     break;
  //   case 3:
  //     if (mask[3] === 'd') {
  //       let m = this._oldValue.substr(0, 2);
  //       let d31 = ['01', '03', '05', '07', '08', '10', '12'];

  //       if (
  //         (/0/.test(this._oldValue[2]) && /[1-9]/.test(this._oldValue[3])) ||
  //         (/[12]/.test(this._oldValue[2]) && /[0-9]/.test(this._oldValue[3])) ||
  //         (m !== '02' && /3/.test(this._oldValue[2]) && /0/.test(this._oldValue[3])) ||
  //         (Array.inArray(m, d31) && /3/.test(this._oldValue[2]) && /1/.test(this._oldValue[3]))
  //       ) {
  //         this._newValue += this._oldValue[3] + mask[5];
  //       }
  //     } else if (mask[3] === 'm') {
  //       let d = parseInt(this._oldValue.substr(0, 2));

  //       if (
  //         (d <= 29 && /[0]/.test(this._oldValue[2]) && /[2]/.test(this._oldValue[3])) ||
  //         (d <= 30 && /[0]/.test(this._oldValue[2]) && /[469]/.test(this._oldValue[3])) ||
  //         (d <= 31 && /[0]/.test(this._oldValue[2]) && /[13578]/.test(this._oldValue[3])) ||
  //         (d <= 30 && /[1]/.test(this._oldValue[2]) && /[1]/.test(this._oldValue[3])) ||
  //         (d <= 31 && /[1]/.test(this._oldValue[2]) && /[02]/.test(this._oldValue[3]))
  //       ) {
  //         this._newValue += this._oldValue[3] + mask[5];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[3])) {
  //         this._newValue += this._oldValue[3] + mask[4];
  //       }
  //     }
  //     break;
  //   case 4:
  //     if (mask[5] === 'm') {
  //       if (/[01]/.test(this._oldValue[4])) {
  //         this._newValue += this._oldValue[4];
  //       }
  //     } else {
  //       if (/[0-2]/.test(this._oldValue[4])) {
  //         this._newValue += this._oldValue[4];
  //       }
  //     }
  //     break;
  //   case 5:
  //     if (mask[6] === 'm') {
  //       if (
  //         (/0/.test(this._oldValue[4]) && /[1-9]/.test(this._oldValue[5])) ||
  //         (/1/.test(this._oldValue[4]) && /[0-2]/.test(this._oldValue[5]))
  //       ) {
  //         this._newValue += this._oldValue[5] + mask[7];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[5])) {
  //         this._newValue += this._oldValue[5];
  //       }
  //     }
  //     break;
  //   case 6:
  //     if (mask[8] === 'd') {
  //       let m = this._oldValue.substr(4, 2);
  //       if (
  //         (m === '02' && /[0-2]/.test(this._oldValue[6])) ||
  //         (m !== '02' && /[0-3]/.test(this._oldValue[6]))
  //       ) {
  //         this._newValue += this._oldValue[6];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[6])) {
  //         this._newValue += this._oldValue[6];
  //       }
  //     }
  //     break;
  //   case 7:
  //     if (mask[9] === 'd') {
  //       let m = this._oldValue.substr(4, 2);
  //       let d31 = ['01', '03', '05', '07', '08', '10', '12'];

  //       if (
  //         (/0/.test(this._oldValue[6]) && /[1-9]/.test(this._oldValue[7])) ||
  //         (/[12]/.test(this._oldValue[6]) && /[0-9]/.test(this._oldValue[7])) ||
  //         (m !== '02' && /3/.test(this._oldValue[6]) && /0/.test(this._oldValue[7])) ||
  //         (Array.inArray(m, d31) && /3/.test(this._oldValue[6]) && /1/.test(this._oldValue[7]))
  //       ) {
  //         this._newValue += this._oldValue[7];
  //       }
  //     } else {
  //       if (/[0-9]/.test(this._oldValue[7])) {
  //         this._newValue += this._oldValue[7];
  //       }
  //     }

  //     if (this._newValue.length === 10) {
  //       let min = this._obj.min.replace(/-/g, '');
  //       let max = this._obj.max.replace(/-/g, '');
  //       let testVal = this._format.dateDB(this._newValue).replace(/-/g, '');

  //       if (min !== undefined && this._obj.min.length > 0 && testVal < min) {
  //         this._newValue = this._format.date(this._obj.min);
  //       } else if (max !== undefined && this._obj.max.length > 0 && testVal > max) {
  //         this._newValue = this._format.date(this._obj.max);
  //       }
  //     }
  //     break;
  // }
  // }
  // }

  /**
   * Define a máscara 00:00:00
   */
  _time() {
    // A carga inicial e a colagem também podem chegar como HH:mm[:ss].
    this._oldValue = this._oldValue.replace(/:/g, '');
    let endCount = (this._oldValue.length < this._mask.length) ? (this._oldValue.length) : (this._mask.length);
    let h, hmin, hmax, m, mmin, mmax, s, smin, smax;
    for (let i = 0; i < endCount; i++) {
      switch (i) {
        case 0:
          if (/[0-2]/.test(this._oldValue[0])) {
            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || this._oldValue[0] >= this._obj.min[0]) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || this._oldValue[0] <= this._obj.max[0])
            ) {
              this._newValue += this._oldValue[0];
            }
          } else if (/[3-9]/.test(this._oldValue[0])) {
            this._newValue += '0' + this._oldValue[0] + ':';
          }
          break;
        case 1:
          if (
            (/[01]/.test(this._oldValue[0]) && /[0-9]/.test(this._oldValue[1])) ||
            (/2/.test(this._oldValue[0]) && /[0-3]/.test(this._oldValue[1]))
          ) {
            h = parseInt(this._oldValue.substr(0, 2));
            hmin = parseInt(this._obj.min.substr(0, 2));
            hmax = parseInt(this._obj.max.substr(0, 2));

            if ((this._obj.min === undefined || this._obj.min.length === 0 || h >= hmin) && (this._obj.max === undefined || this._obj.max.length === 0 || h <= hmax)) {
              this._newValue += this._oldValue[1] + ':';
            }
          }
          break;
        case 2:
          if (this._newValue[2] === ':' && /[0-5]/.test(this._oldValue[2])) {
            h = parseInt(this._oldValue.substr(0, 2));
            hmin = parseInt(this._obj.min.substr(0, 2));
            hmax = parseInt(this._obj.max.substr(0, 2));
            m = parseInt(this._oldValue.substr(2, 1));
            mmin = parseInt(this._obj.min.substr(3, 1));
            mmax = parseInt(this._obj.max.substr(3, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[2];
            }
          }
          break;
        case 3:
          if ((/[0-5]/.test(this._oldValue[2]) && /[0-9]/.test(this._oldValue[3]))) {
            h = parseInt(this._oldValue.substr(0, 2));
            hmin = parseInt(this._obj.min.substr(0, 2));
            hmax = parseInt(this._obj.max.substr(0, 2));
            m = parseInt(this._oldValue.substr(2, 2));
            mmin = parseInt(this._obj.min.substr(3, 2));
            mmax = parseInt(this._obj.max.substr(3, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[3];

              if (this._mask.length > 5) {
                this._newValue += ':';
              }
            }
          }
          break;
        case 4:
          if (this._newValue[5] === ':' && /[0-5]/.test(this._oldValue[4])) {
            m = parseInt(this._oldValue.substr(2, 2));
            mmin = parseInt(this._obj.min.substr(3, 2));
            mmax = parseInt(this._obj.max.substr(3, 2));
            s = parseInt(this._oldValue.substr(4, 1));
            smin = parseInt(this._obj.min.substr(5, 1));
            smax = parseInt(this._obj.max.substr(5, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || h < mmax)
            ) {
              this._newValue += this._oldValue[4];
            }
          }
          break;
        case 5:
          if ((/[0-5]/.test(this._oldValue[4]) && /[0-9]/.test(this._oldValue[5]))) {
            m = parseInt(this._oldValue.substr(2, 2));
            mmin = parseInt(this._obj.min.substr(3, 2));
            mmax = parseInt(this._obj.max.substr(3, 2));
            s = parseInt(this._oldValue.substr(4, 2));
            smin = parseInt(this._obj.min.substr(5, 2));
            smax = parseInt(this._obj.max.substr(5, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || m < mmax)
            ) {
              this._newValue += this._oldValue[5];
            }
          }
          break;
      }
    }
  }

  /**
 * Define a máscara 000d00h00m00s
 */
  _timeDays() {
    let endCount = (this._oldValue.length < this._mask.length) ? (this._oldValue.length) : (this._mask.length);
    let d, dmin, dmax, h, hmin, hmax, m, mmin, mmax, s, smin, smax;
    for (let i = 0; i < endCount; i++) {
      switch (i) {
        case 0:
          if (/[0-9]/.test(this._oldValue[0])) {
            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || this._oldValue[0] >= this._obj.min[0]) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || this._oldValue[0] <= this._obj.max[0])
            ) {
              this._newValue += this._oldValue[0];
            }
          }
          break;
        case 1:
          if (/[0-9]/.test(this._oldValue[1])) {
            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || this._oldValue[1] >= this._obj.min[1]) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || this._oldValue[1] <= this._obj.max[1])
            ) {
              this._newValue += this._oldValue[1];
            }
          }
          break;
        case 2:
          if (/[0-9]/.test(this._oldValue[2])) {
            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || this._oldValue[2] >= this._obj.min[2]) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || this._oldValue[2] <= this._obj.max[2])
            ) {
              this._newValue += this._oldValue[2] + 'd';
            }
          }
          break;
        case 3:
          if (this._newValue[3] === 'd' && /[0-2]/.test(this._oldValue[3])) {
            d = parseInt(this._oldValue.substr(0, 3));
            dmin = parseInt(this._obj.min.substr(0, 3));
            dmax = parseInt(this._obj.max.substr(0, 3));
            h = parseInt(this._oldValue.substr(3, 1));
            hmin = parseInt(this._obj.min.substr(4, 1));
            hmax = parseInt(this._obj.max.substr(4, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (d === dmin && h >= hmin) || d > dmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (d === dmax && h <= hmax) || d < dmax)
            ) {
              this._newValue += this._oldValue[3];
            }
          }
          break;
        case 4:
          if (
            (/[01]/.test(this._oldValue[3]) && /[0-9]/.test(this._oldValue[4])) ||
            (/2/.test(this._oldValue[3]) && /[0-3]/.test(this._oldValue[4]))
          ) {
            d = parseInt(this._oldValue.substr(0, 3));
            dmin = parseInt(this._obj.min.substr(0, 3));
            dmax = parseInt(this._obj.max.substr(0, 3));
            h = parseInt(this._oldValue.substr(3, 2));
            hmin = parseInt(this._obj.min.substr(4, 2));
            hmax = parseInt(this._obj.max.substr(4, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (d === dmin && h >= hmin) || d > dmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (d === dmax && h <= hmax) || d < dmax)
            ) {
              this._newValue += this._oldValue[4] + 'h';
            }
          }
          break;
        case 5:
          if (this._newValue[6] === 'h' && /[0-5]/.test(this._oldValue[5])) {
            h = parseInt(this._oldValue.substr(3, 2));
            hmin = parseInt(this._obj.min.substr(4, 2));
            hmax = parseInt(this._obj.max.substr(4, 2));
            m = parseInt(this._oldValue.substr(5, 1));
            mmin = parseInt(this._obj.min.substr(7, 1));
            mmax = parseInt(this._obj.max.substr(7, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[5];
            }
          }
          break;
        case 6:
          if ((/[0-5]/.test(this._oldValue[5]) && /[0-9]/.test(this._oldValue[6]))) {
            h = parseInt(this._oldValue.substr(3, 2));
            hmin = parseInt(this._obj.min.substr(4, 2));
            hmax = parseInt(this._obj.max.substr(4, 2));
            m = parseInt(this._oldValue.substr(5, 2));
            mmin = parseInt(this._obj.min.substr(7, 2));
            mmax = parseInt(this._obj.max.substr(7, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[6] + 'm';
            }
          }
          break;
        case 7:
          if (this._newValue[9] === 'm' && /[0-5]/.test(this._oldValue[7])) {
            m = parseInt(this._oldValue.substr(5, 2));
            mmin = parseInt(this._obj.min.substr(10, 2));
            mmax = parseInt(this._obj.max.substr(10, 2));
            s = parseInt(this._oldValue.substr(7, 1));
            smin = parseInt(this._obj.min.substr(10, 1));
            smax = parseInt(this._obj.max.substr(10, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || m < mmax)
            ) {
              this._newValue += this._oldValue[7];
            }
          }
          break;
        case 8:
          if ((/[0-5]/.test(this._oldValue[7]) && /[0-9]/.test(this._oldValue[8]))) {
            m = parseInt(this._oldValue.substr(5, 2));
            mmin = parseInt(this._obj.min.substr(10, 2));
            mmax = parseInt(this._obj.max.substr(10, 2));
            s = parseInt(this._oldValue.substr(7, 2));
            smin = parseInt(this._obj.min.substr(10, 2));
            smax = parseInt(this._obj.max.substr(10, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || m < mmax)
            ) {
              this._newValue += this._oldValue[8] + 's';
            }
          }
          break;
      }
    }
  }

  /**
   * Define a máscara 000:00:00
   */
  _timeTotal() {
    let endCount = (this._oldValue.length < this._mask.length) ? (this._oldValue.length) : (this._mask.length);
    let h, hmin, hmax, m, mmin, mmax, s, smin, smax;
    for (let i = 0; i < endCount; i++) {
      switch (i) {
        case 0:
          if (/[0-9]/.test(this._oldValue[0])) {
            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || this._oldValue[0] >= this._obj.min[0]) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || this._oldValue[0] <= this._obj.max[0])
            ) {
              this._newValue += this._oldValue[0];
            }
          }
          break;
        case 1:
          if (/[0-9]/.test(this._oldValue[1])) {
            h = parseInt(this._oldValue.substr(0, 2));
            hmin = parseInt(this._obj.min.substr(0, 2));
            hmax = parseInt(this._obj.max.substr(0, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || h >= hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || h <= hmax)
            ) {
              this._newValue += this._oldValue[1];
            }
          }
          break;
        case 2:
          if (/[0-9]/.test(this._oldValue[2])) {
            h = parseInt(this._oldValue.substr(0, 3));
            hmin = parseInt(this._obj.min.substr(0, 3));
            hmax = parseInt(this._obj.max.substr(0, 3));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || h >= hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || h <= hmax)
            ) {
              this._newValue += this._oldValue[2] + ':';
            }
          }
          break;
        case 3:
          if (this._newValue[3] === ':' && /[0-5]/.test(this._oldValue[3])) {
            h = parseInt(this._oldValue.substr(0, 3));
            hmin = parseInt(this._obj.min.substr(0, 3));
            hmax = parseInt(this._obj.max.substr(0, 3));
            m = parseInt(this._oldValue.substr(3, 1));
            mmin = parseInt(this._obj.min.substr(4, 1));
            mmax = parseInt(this._obj.max.substr(4, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[3];
            }
          }
          break;
        case 4:
          if ((/[0-5]/.test(this._oldValue[3]) && /[0-9]/.test(this._oldValue[4]))) {
            h = parseInt(this._oldValue.substr(0, 3));
            hmin = parseInt(this._obj.min.substr(0, 3));
            hmax = parseInt(this._obj.max.substr(0, 3));
            m = parseInt(this._oldValue.substr(3, 2));
            mmin = parseInt(this._obj.min.substr(4, 2));
            mmax = parseInt(this._obj.max.substr(4, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (h === hmin && m >= mmin) || h > hmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (h === hmax && m <= mmax) || h < hmax)
            ) {
              this._newValue += this._oldValue[4];

              if (this._mask.length > 6) {
                this._newValue += ':';
              }
            }
          }
          break;
        case 5:
          if (this._newValue[6] === ':' && /[0-5]/.test(this._oldValue[5])) {
            m = parseInt(this._oldValue.substr(3, 2));
            mmin = parseInt(this._obj.min.substr(4, 2));
            mmax = parseInt(this._obj.max.substr(4, 2));
            s = parseInt(this._oldValue.substr(5, 1));
            smin = parseInt(this._obj.min.substr(7, 1));
            smax = parseInt(this._obj.max.substr(7, 1));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || m < mmax)
            ) {
              this._newValue += this._oldValue[5];
            }
          }
          break;
        case 6:
          if ((/[0-5]/.test(this._oldValue[5]) && /[0-9]/.test(this._oldValue[6]))) {
            m = parseInt(this._oldValue.substr(3, 2));
            mmin = parseInt(this._obj.min.substr(4, 2));
            mmax = parseInt(this._obj.max.substr(4, 2));
            s = parseInt(this._oldValue.substr(5, 2));
            smin = parseInt(this._obj.min.substr(7, 2));
            smax = parseInt(this._obj.max.substr(7, 2));

            if (
              (this._obj.min === undefined || this._obj.min.length === 0 || (m === mmin && s >= smin) || m > mmin) &&
              (this._obj.max === undefined || this._obj.max.length === 0 || (m === mmax && s <= smax) || m < mmax)
            ) {
              this._newValue += this._oldValue[6];
            }
          }
          break;
      }
    }
  }
}
